import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
export default function Dialog({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  /** One line under the title: what saving here changes, or what the record is. */
  description?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    const previous = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    el?.showModal();
    // showModal() lands focus on the first focusable control, which is the close
    // button; the first field is where typing starts.
    // With no field, the title takes focus: showModal() would otherwise ring the close button.
    const focusFirst = () =>
      (el?.querySelector<HTMLElement>(".record-dialog-content input:not([type=hidden]):not([disabled]), .record-dialog-content select, .record-dialog-content textarea")
        ?? el?.querySelector<HTMLElement>(".record-dialog-title h2"))?.focus({ preventScroll: true });
    const t = window.setTimeout(focusFirst, 30);
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = previousOverflow;
      el?.close();
      previous?.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="record-dialog"
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // A click on the backdrop (the dialog element itself, outside its content) closes it.
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        const bounds=e.currentTarget.getBoundingClientRect();
        if(e.clientX<bounds.left||e.clientX>bounds.right||e.clientY<bounds.top||e.clientY>bounds.bottom) onClose();
      }}
    >
      <div className="record-dialog-head">
        <div className="record-dialog-title">
          <h2 tabIndex={-1}>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        <button
          type="button"
          className="icon-button record-dialog-close"
          onClick={onClose}
          aria-label="Close dialog"
        >
          <X aria-hidden="true" />
        </button>
      </div>
      <div className="record-dialog-content">{children}</div>
    </dialog>
  );
}
